import {useRef, useState} from 'react';
import ReactCrop, {
  centerCrop,
  makeAspectCrop,
  type Crop,
} from 'react-image-crop';
import {Props} from './types';
import {Box, Typography} from '@mui/material';
import {ButtonV2, Button, UploadInput} from '@jobseeker/lotus/elements';
import {useWrappedTheme} from '@jobseeker/lotus/hooks';
import {Image} from '@jobseeker/lotus/components/Image';
import {RotateLeftGreen, RotateLeftOrange} from '@jobseeker/lotus/icons';
import {useIsLargeDevice} from '@jobseeker/lotus/hooks';
import Loader from '@jobseeker/lotus/components/Loader';
const isAppIimjobs = process.env.NEXT_PUBLIC_APP_NAME === 'iimjobs';

/* import 'react-image-crop/dist/ReactCrop.css';
add import of above line in web-app/pages wherever used */

const ImageCropper = (props: Props) => {
  const {
    imgSrc,
    aspectRatio,
    minimumDimension,
    setCroppedImage,
    loading,
    setLoading,
    handlePicFileUpload,
  } = props;
  const [crop, setCrop] = useState<Crop>();
  const [rotationDeg, setRotationDeg] = useState(0);
  const imgRef = useRef<HTMLImageElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const theme = useWrappedTheme();
  const isAboveMd = useIsLargeDevice('md');
  const TO_RADIANS = Math.PI / 180;

  const hiddenChangeFileInput = useRef<HTMLInputElement>(null);

  const onImgLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const {width, height} = e?.currentTarget;
    const crop = makeAspectCrop(
      {
        unit: 'px',
        width: minimumDimension,
      },
      aspectRatio,
      width,
      height
    );
    const centeredCrop = centerCrop(crop, width, height);
    setCrop(centeredCrop);
  };

  const setCanvasPreview = (
    image: React.RefObject<HTMLImageElement>,
    canvas: React.RefObject<HTMLCanvasElement>,
    crop: Crop
  ) => {
    if (!image.current || !canvas.current || !crop) {
      return;
    }

    const ctx = canvas.current.getContext('2d');
    if (!ctx) {
      return;
    }

    // Calculate scaling factors from display size to natural size
    const scaleX = image.current.naturalWidth / image.current.width;
    const scaleY = image.current.naturalHeight / image.current.height;

    // Set canvas size to match the cropped area
    canvas.current.width = Math.floor(crop.width * scaleX);
    canvas.current.height = Math.floor(crop.height * scaleY);

    // Clear the canvas
    ctx.clearRect(0, 0, canvas.current.width, canvas.current.height);

    // Set high quality rendering
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';

    // Save the current context state
    ctx.save();

    // Calculate crop coordinates in natural image dimensions
    const cropX = crop.x * scaleX;
    const cropY = crop.y * scaleY;
    const cropWidth = crop.width * scaleX;
    const cropHeight = crop.height * scaleY;

    // Handle rotation if needed
    if (rotationDeg !== 0) {
      const rotateRads = rotationDeg * TO_RADIANS;
      const centerX = cropWidth / 2;
      const centerY = cropHeight / 2;

      // Move to center of canvas
      ctx.translate(centerX, centerY);
      // Apply rotation
      ctx.rotate(rotateRads);
      // Move back
      ctx.translate(-centerX, -centerY);
    }

    // Draw the cropped portion of the image
    ctx.drawImage(
      image.current,
      cropX, // Source X
      cropY, // Source Y
      cropWidth, // Source width
      cropHeight, // Source height
      0, // Destination X
      0, // Destination Y
      cropWidth, // Destination width
      cropHeight // Destination height
    );

    // Restore the context state
    ctx.restore();
  };

  return (
    <Box
      display={'flex'}
      justifyContent={'center'}
      alignItems={'center'}
      flexDirection={'column'}
      width={'100%'}
      gap={3}
    >
      <Box
        display={'flex'}
        justifyContent={'center'}
        alignItems={'center'}
        sx={{
          background: '#333333',
        }}
        width={isAboveMd ? 600 : '100%'}
        height={300}
      >
        <ReactCrop
          crop={crop}
          aspect={aspectRatio}
          keepSelection
          minWidth={50}
          onChange={c => setCrop(c)}
        >
          <img
            ref={imgRef}
            src={imgSrc}
            alt="cropper"
            style={{
              width: '100%',
              maxWidth: isAboveMd ? 550 : '100%',
              height: '100%',
              maxHeight: 300,
              transform: `rotate(${rotationDeg}deg)`,
            }}
            crossOrigin="anonymous"
            onLoad={onImgLoad}
          />
        </ReactCrop>
      </Box>
      <Box
        width={'100%'}
        display={'flex'}
        justifyContent={'space-between'}
        alignItems={isAboveMd ? 'center' : 'flex-start'}
        flexWrap={'wrap'}
        flexDirection={isAboveMd ? 'row' : 'column'}
        gap={isAboveMd ? 1 : 2}
      >
        <Box
          display={'flex'}
          justifyContent={'space-between'}
          alignItems={'center'}
          gap={1.5}
          width={isAboveMd ? 'fit-content' : '100%'}
        >
          <Box
            display={'flex'}
            justifyContent={'space-between'}
            alignItems={'center'}
            gap={0.5}
            sx={{
              cursor: 'pointer',
            }}
            onClick={() => {
              setRotationDeg(rotationDeg - 90);
            }}
          >
            <Image
              src={isAppIimjobs ? RotateLeftGreen : RotateLeftOrange}
              width={18}
              height={18}
              alt="rotate-left"
            />
            <Typography
              variant="subtitle2"
              color="primary.main"
              lineHeight={'21px'}
              sx={{
                ':hover': {
                  textDecoration: 'underline',
                },
              }}
            >
              Rotate Left
            </Typography>
          </Box>
          <Box
            display={'flex'}
            justifyContent={'space-between'}
            alignItems={'center'}
            gap={0.5}
            sx={{
              cursor: 'pointer',
            }}
            onClick={() => {
              setRotationDeg(rotationDeg + 90);
            }}
          >
            <Image
              src={isAppIimjobs ? RotateLeftGreen : RotateLeftOrange}
              width={18}
              height={18}
              alt="rotate-right"
              style={{
                transform: 'scaleX(-1)',
              }}
            />
            <Typography
              variant="subtitle2"
              color="primary.main"
              lineHeight={'21px'}
              sx={{
                ':hover': {
                  textDecoration: 'underline',
                },
              }}
            >
              Rotate Right
            </Typography>
          </Box>
        </Box>
        <Box
          display={'flex'}
          justifyContent={'space-between'}
          alignItems={'center'}
          gap={2}
          width={isAboveMd ? 'fit-content' : '100%'}
        >
          <Button
            component="label"
            disableRipple
            sx={{
              px: 1.5,
              py: 1,
              fontSize: theme.typography.pxToRem(18),
              fontWeight: 500,
              lineHeight: '24px',
              color: 'primary.main',
              borderRadius: isAppIimjobs ? theme.spacing(8) : '4px',
              border: '1px solid',
              borderColor: 'primary.main',
              whiteSpace: 'nowrap',
            }}
            variant="outlined"
            label="change-photo"
            size="small"
            onClick={() => {
              hiddenChangeFileInput?.current?.click();
              setLoading(false);
            }}
          >
            Change Photo
            <UploadInput
              ref={hiddenChangeFileInput}
              handleUpload={handlePicFileUpload}
              accept=".png,.jpg,.jpeg"
            />
          </Button>
          <ButtonV2
            disabled={loading}
            sx={{
              px: 6,
              py: 1,
              backgroundColor: isAppIimjobs
                ? theme.palette.secondary.main
                : theme.palette.primary.main,
              fontSize: theme.typography.pxToRem(18),
              fontWeight: 500,
              lineHeight: '24px',
              borderRadius: isAppIimjobs ? theme.spacing(8) : '4px',
              width: '160px',
              '&:hover': {
                backgroundColor: 'primary.main',
              },
            }}
            variant="contained"
            size="large"
            onClick={() => {
              setLoading(true);
              crop && setCanvasPreview(imgRef, canvasRef, crop);
              if (canvasRef?.current) {
                const dataUrl = canvasRef.current.toDataURL('image/jpeg', 0.5);
                if (dataUrl) {
                  setCroppedImage(dataUrl);
                }
              }
              setLoading(false);
            }}
          >
            {loading ? <Loader size={20} /> : 'Upload'}
          </ButtonV2>
        </Box>
      </Box>
      {crop && <canvas ref={canvasRef} style={{display: 'none'}}></canvas>}
    </Box>
  );
};

export default ImageCropper;
